import { useEffect, useState } from "react";
import { CheckCircle2, Clock3 } from "lucide-react";
import { fetchPoHelpdeskQueries } from "../../../services/POMismatchService";
import { PoMismatchMetric } from "./PoMismatchMetric";
import { Ticket } from "./types";

export function HelpdeskQueueTab({ onTicketSelect }: { onTicketSelect: (ticket: Ticket) => void }) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    fetchPoHelpdeskQueries()
      .then((queries) => {
        if (isMounted) setTickets(queries);
      })
      .catch(() => {
        if (isMounted) setError("Unable to load helpdesk queries from the API.");
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const ticketsToday = 5;
  const mismatchTickets = 4;
  const agentResolvableTickets = 3;
  const cashImpact = tickets.reduce((total, ticket) => {
    const numericValue = Number(ticket.value.replace(/[^0-9.-]+/g, ""));
    return total + (Number.isFinite(numericValue) ? numericValue : 0);
  }, 0);
  const formatCurrency = (value: number) => `$${value.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  return (
    <>
      <div className="po-mismatch__kpis">
        <PoMismatchMetric label="Tickets today" value={String(ticketsToday)} detail="helpdesk queue" tone="purple" />
        <PoMismatchMetric label="PO/invoice mismatches" value={String(mismatchTickets)} detail={`${Math.round((mismatchTickets / Math.max(tickets.length, 1)) * 100)}% of volume`} tone="blue" />
        <PoMismatchMetric label="Agent-resolvable" value={String(agentResolvableTickets)} detail={`${Math.round((agentResolvableTickets / Math.max(mismatchTickets, 1)) * 100)}% of mismatches`} tone="green" />
        <PoMismatchMetric label="Cash impact in queue" value={formatCurrency(cashImpact)} detail="blocked invoices" tone="orange" />
      </div>
      <section className="po-mismatch__queue">
        <div className="po-mismatch__queue-head"><div><h3>Helpdesk Queue - Event-Triggered</h3><p>Each new ticket fires an event to the agent. Tickets are classified from the summary and description; only mismatch intents above the confidence bar are worked automatically.</p></div></div>
        {error && <p role="status">{error}</p>}
        {isLoading && <p role="status">Loading helpdesk queries...</p>}
        <div className="po-mismatch__table-wrap">
          <table className="po-mismatch__table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Supplier</th>
                <th>Summary</th>
                <th>Raised</th>
                <th>Priority</th>
                <th>Blocked value</th>
                <th>AI classification</th>
              </tr>
            </thead>
              <tbody>{tickets.map((ticket) => <tr key={ticket.id} className="po-mismatch__table-row">
                  <td className="po-mismatch__ticket">
                    <button type="button" className="po-mismatch__ticket-link" onClick={()=> onTicketSelect(ticket)}>{ticket.id}</button>
                  </td>
                  <td className="po-mismatch__supplier-cell">{ticket.supplier}</td>
                  <td className="po-mismatch__summary-cell">{ticket.summary}</td>
                  <td className="po-mismatch__raised-cell">
                    <span>{ticket.raised}</span>
                  </td>
                  <td>
                    <span className={`po-mismatch__priority po-mismatch__priority--${ticket.priority.toLowerCase()}`}>{ticket.priority}</span>
                  </td>
                  <td className="po-mismatch__value">{ticket.value}</td>
                  <td>{ticket.status === "classified" ? <span className="po-mismatch__classification">
                      <span className="mr-1">✦</span> {ticket.classification} · {ticket.confidence}
                    </span> : <span className={`po-mismatch__classification po-mismatch__classification--${ticket.status==="no-po" ? "muted" : "excluded" }`}>{ticket.classification}</span>} </td>
                </tr>)}{!isLoading && !error && tickets.length === 0 && <tr>
                  <td colSpan={7} role="status">No helpdesk queries found.</td>
                </tr>} </tbody>
            </table>
        </div>
        <div className="po-mismatch__callout">
          <div className="po-mismatch__callout-head">
            <span className="po-mismatch__ai-badge">+AI</span>
            <span className="po-mismatch__callout-label">Queue triage</span>
            </div>
            <p>Three tickets are clean mismatch cases and are picked up automatically. <span className="po-mismatch__callout-link">TKT-2345</span> has no PO reference and scores 74% - below the bar - so it is handed to a specialist with the supplier, amount and date already resolved. <span className="po-mismatch__callout-link">TKT-2347</span> is excluded as non-procurement. Open <span className="po-mismatch__callout-link">TKT-2346</span> to see the full diagnosis.</p></div>
      </section>
    </>
  );
}